import { isHTMLOrSVGElement } from '../dom/node';
import { isParagraphElement } from '../dom/paragraph';
import {
  applyStyleToElement,
  applyStyleToRange,
  createMapToComputedStyle,
  getInheritedStyleForNode,
  hasInheritedStyleForNode,
  removeStyleFromElement,
  removeStyleFromRange,
  StyleSpec,
} from '../dom/style';
import { getParagraphIndentLevel } from '../paragraphIndent';

const FONT_SIZE_SPEC: StyleSpec = {
  stylePropertyName: 'font-size',
  mapStylePropertyValue: createMapToComputedStyle('font-size'),
  fontElementAttributeName: 'size',
  mapFontElementAttributeValue: createMapToComputedStyle('font-size'),
  isValueEqual: (fontSizeA, fontSizeB) => compareFontSize(fontSizeA, fontSizeB) === 0,
  shouldPaintElementIfValueDiffers: node => isParagraphElement(node) && getParagraphIndentLevel(node) > 0,
};

export function compareFontSize(fontSizeA: string, fontSizeB: string): number {
  if (fontSizeA === fontSizeB) {
    return 0;
  }

  const partsA = parseFontSize(fontSizeA);
  const partsB = parseFontSize(fontSizeB);
  if (!partsA || !partsB || partsA.unit !== partsB.unit) {
    return 1;
  }

  const difference = partsB.value - partsA.value;
  return Math.abs(difference) < 0.01 ? 0 : difference;
}

export function parseFontSize(fontSize: string): { value: number; unit: string } | undefined {
  // https://www.w3.org/TR/CSS21/grammar.html
  const match = fontSize.match(/^(\d+|\d*\.\d+)(\D+)$/);
  if (!match) {
    return undefined;
  }

  return {
    value: parseFloat(match[1]),
    unit: match[2].toLowerCase(),
  };
}

export function nodeHasFontSize(node: Node, container: Node): boolean {
  return hasInheritedStyleForNode(container, node, FONT_SIZE_SPEC);
}

export function getFontSizeForNode(node: Node, container: Node): string {
  return getInheritedStyleForNode(container, node, FONT_SIZE_SPEC);
}

export function removeFontSizeFromElement(element: Element): Node | null {
  return isHTMLOrSVGElement(element) ? removeStyleFromElement(element, FONT_SIZE_SPEC) : element;
}

export function removeFontSizeFromRange(container: Node, range: Range): Range {
  return removeStyleFromRange(container, range, FONT_SIZE_SPEC);
}

export function applyFontSizeToElement(element: HTMLElement | SVGElement, fontSize: string): void {
  applyStyleToElement(element, FONT_SIZE_SPEC, fontSize);
}

export function setFontSizeInRange(container: Node, range: Range, fontSize: string): Range {
  return applyStyleToRange(container, range, FONT_SIZE_SPEC, fontSize);
}
